import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardSubtitle, CardTitle, Button, Field, Input } from '@keobook/ui';
import { type LedgerEntry } from '@keobook/shared';
import { api } from '../lib/api';
import { useMoneyFormatter } from '../lib/useCurrency';
import { Layout } from '../components/Layout';
import { CoaPicker } from '../components/CoaPicker';

/** YYYY-MM-DD in UTC, N days back from today. */
function isoDateOffset(daysBack: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - daysBack);
  return d.toISOString().slice(0, 10);
}

const PRESETS: Array<{ label: string; daysBack: number | null }> = [
  { label: 'Last 30 days', daysBack: 30 },
  { label: 'Last 90 days', daysBack: 90 },
  { label: 'Year to date', daysBack: -1 }, // sentinel; see ytdFrom
  { label: 'Last 12 months', daysBack: 365 },
  { label: 'All time', daysBack: null },
];

function ytdFrom(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-01-01`;
}

/**
 * General Ledger — the chronological log of every posted journal entry,
 * regardless of source (AI, CPA-approved, customer self-posted, imported,
 * system).
 *
 * Per Brickstone feedback 2026-06-22: Activity is the REVIEW queue (what
 * needs my attention). GL is the WRITTEN HISTORY (what's on the books).
 * Two pages, no filter toggle — easier to point George at "the GL" without
 * explaining what mode is active.
 *
 * Today this is a simple infinite-scroll list — same shape as Activity's
 * old bottom section, just without the "Send to review" button (only AI
 * entries get that, and AI entries here are intentionally rendered as
 * read-only history; if George wants to act on one, he goes to Activity).
 *
 * Future work: filters by account / source / date range. For now, the
 * Reports page's GL Summary report is the place for analytical views.
 */
const SOURCE_LABEL: Record<string, string> = {
  ai: 'AI',
  staff: 'CPA',
  migration: 'Imported',
  customer_api: 'API',
  system: 'System',
};

export function GeneralLedger() {
  const fmt = useMoneyFormatter();
  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Default to last 90 days so the first page load isn't unbounded.
  // George's books in prod already hold ~3K entries; without a default
  // window the browser pulls 50 at a time and the user can scroll forever.
  const [from, setFrom] = useState<string>(() => isoDateOffset(90));
  const [to, setTo] = useState<string>(() => isoDateOffset(0));
  // Apply token — incremented on every Apply click so loadFirst's
  // useCallback ALWAYS regenerates (and thus useEffect refires)
  // even if the from/to values haven't changed since the last apply.
  // This is the fix for "Apply button doesn't fire": React skips
  // re-renders when setState gets an identical value, and that was
  // making the loadFirst callback's deps look unchanged.
  const [applyToken, setApplyToken] = useState(0);

  const loadFirst = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      // No source filter — GL shows every posted entry.
      const res = await api.getLedgerEntries({
        limit: 50,
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
      });
      setEntries(res.entries);
      setCursor(res.nextCursor);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not load ledger.');
    } finally {
      setLoading(false);
    }
    // applyToken intentionally part of deps so an explicit Apply click
    // refetches even when from/to are unchanged.
  }, [from, to, applyToken]);

  useEffect(() => {
    void loadFirst();
  }, [loadFirst]);

  async function loadMore() {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const res = await api.getLedgerEntries({
        limit: 50,
        cursor,
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
      });
      setEntries((prev) => [...prev, ...res.entries]);
      setCursor(res.nextCursor);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not load more.');
    } finally {
      setLoadingMore(false);
    }
  }

  function applyPreset(preset: (typeof PRESETS)[number]) {
    const nextTo = isoDateOffset(0);
    let nextFrom = '';
    if (preset.daysBack === null) {
      nextFrom = '';
    } else if (preset.daysBack === -1) {
      nextFrom = ytdFrom();
    } else {
      nextFrom = isoDateOffset(preset.daysBack);
    }
    setFrom(nextFrom);
    setTo(nextTo);
  }

  // The filter card description — match George's mental model: "what
  // window am I looking at right now?"
  const windowDescription = useMemo(() => {
    if (!from && !to) return 'All time';
    if (from && !to) return `From ${from}`;
    if (!from && to) return `Through ${to}`;
    return `${from} → ${to}`;
  }, [from, to]);

  return (
    <Layout>
      <div className="kb-page" style={{ maxWidth: 920, margin: '0 auto', padding: '2rem 1.5rem' }}>
        <h1
          style={{
            fontSize: 26,
            fontWeight: 600,
            color: 'var(--kb-gray-900)',
            margin: '0 0 0.5rem',
          }}
        >
          General Ledger
        </h1>
        <p style={{ fontSize: 13, color: 'var(--kb-gray-400)', margin: '0 0 1.25rem' }}>
          Every posted journal entry, newest first. Analytical views (per-account totals, balances)
          live on the Reports page.
        </p>

        {/* Date filter card. Server-side filter — at ~3K entries per
            tenant, client-side would mean loading the world on first
            paint. The preset row is the fast path; the custom range
            below gives George explicit YYYY-MM-DD inputs. */}
        <Card>
          <CardTitle>Date range</CardTitle>
          <CardSubtitle>
            Showing: <strong>{windowDescription}</strong>
          </CardSubtitle>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
            {PRESETS.map((p) => {
              const isActive =
                (p.daysBack === null && !from && !to) ||
                (p.daysBack === -1 && from === ytdFrom()) ||
                (p.daysBack !== null && p.daysBack !== -1 && from === isoDateOffset(p.daysBack));
              return (
                <Button
                  key={p.label}
                  size="sm"
                  variant={isActive ? 'primary' : 'ghost'}
                  onClick={() => applyPreset(p)}
                >
                  {p.label}
                </Button>
              );
            })}
          </div>
          {/* Custom range — date pickers + an explicit Apply button. */}
          <div
            style={{
              display: 'flex',
              gap: 12,
              marginTop: 14,
              alignItems: 'flex-end',
              flexWrap: 'wrap',
            }}
          >
            <Field label="From">
              <Input type="date" value={from} onChange={(e) => setFrom(e.currentTarget.value)} />
            </Field>
            <Field label="To">
              <Input type="date" value={to} onChange={(e) => setTo(e.currentTarget.value)} />
            </Field>
          </div>
          {/* Apply lives on its own row so flex-wrap on narrow viewports
              never pushes it off-screen. The full width + explicit
              Button (not nested inside any Field) sidesteps the earlier
              regression where the button vanished or was clipped. */}
          <div style={{ marginTop: 12 }}>
            <Button size="sm" onClick={() => setApplyToken((n) => n + 1)}>
              Apply
            </Button>
          </div>
        </Card>

        {error && (
          <div role="alert" className="kb-form-error">
            {error}
          </div>
        )}

        {loading ? (
          <Card>
            <CardSubtitle>Loading…</CardSubtitle>
          </Card>
        ) : entries.length === 0 ? (
          <Card>
            <CardTitle>No posted entries yet</CardTitle>
            <CardSubtitle>
              As bank statements get processed and entries get approved, they land here.
            </CardSubtitle>
          </Card>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {entries.map((e) => (
              <Card key={e.id}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 12,
                    alignItems: 'baseline',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600, color: 'var(--kb-gray-900)' }}>
                      {e.description}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--kb-gray-400)', marginTop: 2 }}>
                      {new Date(e.postedAt).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </div>
                  </div>
                  <span className="kb-pill kb-pill--neutral">
                    {SOURCE_LABEL[e.source] ?? e.source}
                  </span>
                </div>
                <table className="kb-report" style={{ marginTop: 10 }}>
                  <tbody>
                    {e.lines.map((l, i) => (
                      <tr key={`${e.id}-${i}`}>
                        <td>
                          <CoaPicker
                            currentCode={l.accountCode}
                            currentLabel={l.accountName}
                            target={{ mode: 'ledger', entryId: e.id, lineId: l.id }}
                            onDone={() => void loadFirst()}
                          />
                          {l.memo ? <span className="kb-muted"> — {l.memo}</span> : null}
                        </td>
                        <td className="kb-report__amt">
                          {l.amountCents > 0 ? fmt(l.amountCents) : ''}
                        </td>
                        <td className="kb-report__amt">
                          {l.amountCents < 0 ? fmt(-l.amountCents) : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            ))}
            {cursor && (
              <Button variant="ghost" size="sm" disabled={loadingMore} onClick={loadMore}>
                {loadingMore ? 'Loading…' : 'Load more'}
              </Button>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
